import React, { useState, useEffect } from 'react';
import TalktasticVoiceGame from './TalktasticVoiceGame';
import HomePage from './HomePage';
import './App.css';

function App() {
  const [gameStarted, setGameStarted] = useState(false);
  const [gameMode, setGameMode] = useState('single'); // 'single' or 'multi'
  const [showRules, setShowRules] = useState(false); // Rules display state
  const [userInfo, setUserInfo] = useState({
    username: '',
    avatarIndex: 0,
    learningLanguage: 'english',
    nativeLanguage: 'chinese',
    currentScene: 'IELTS'
  });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Handle mouse movement
  useEffect(() => {
    const handleMouseMove = (event) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Listen for scene change event
  useEffect(() => {
    // Initialize user info
    setUserInfo({
      username: 'Guest',
      currentScene: 'IELTS'
    });
    
    // Listen for scene change event
    const handleSceneChange = (event) => {
      setUserInfo(prevUserInfo => ({
        ...prevUserInfo,
        currentScene: event.detail.scene
      }));
    };
    
    window.addEventListener('sceneChange', handleSceneChange);
    return () => window.removeEventListener('sceneChange', handleSceneChange);
  }, []);

  // Language options moved to HomePage.js

  const handleStartSinglePlayer = (username, avatarIndex, learningLanguage, nativeLanguage) => {
    // Set default scene based on selected language
    let defaultScene;
    switch(learningLanguage) {
      case 'english':
        defaultScene = 'IELTS';
        break;
      case 'french':
        defaultScene = 'DELF';
        break;
      case 'spanish':
        defaultScene = 'DELE';
        break;
      case 'japanese':
        defaultScene = 'JLPT';
        break;
      case 'korean':
        defaultScene = 'TOPIK';
        break;
      case 'portuguese':
        defaultScene = 'CAPLE';
        break;
      case 'german':
        defaultScene = 'TestDaF';
        break;
      case 'italian':
        defaultScene = 'CELI';
        break;
      case 'russian':
        defaultScene = 'TORFL';
        break;
      case 'arabic':
        defaultScene = 'ALPT';
        break;
      case 'dutch':
        defaultScene = 'CNaVT';
        break;
      default:
        defaultScene = 'IELTS';
    }
    
    setUserInfo({
      username,
      avatarIndex,
      learningLanguage,
      nativeLanguage,
      currentScene: defaultScene
    });
    setGameMode('single');
    setGameStarted(true);
  };

  const handleStartMultiPlayer = (username, avatarIndex, learningLanguage, nativeLanguage) => {
    // Set default scene based on selected language
    let defaultScene;
    switch(learningLanguage) {
      case 'english':
        defaultScene = 'IELTS';
        break;
      case 'french':
        defaultScene = 'DELF';
        break;
      case 'spanish':
        defaultScene = 'DELE';
        break;
      case 'japanese':
        defaultScene = 'JLPT';
        break;
      case 'korean':
        defaultScene = 'TOPIK';
        break;
      case 'portuguese':
        defaultScene = 'CAPLE';
        break;
      case 'german':
        defaultScene = 'TestDaF';
        break;
      case 'italian':
        defaultScene = 'CELI';
        break;
      case 'russian':
        defaultScene = 'TORFL';
        break;
      case 'arabic':
        defaultScene = 'ALPT';
        break;
      case 'dutch':
        defaultScene = 'CNaVT';
        break;
      default:
        defaultScene = 'IELTS';
    }
    
    setUserInfo({
      username,
      avatarIndex,
      learningLanguage,
      nativeLanguage,
      currentScene: defaultScene
    });
    setGameMode('multi');
    setGameStarted(true);
  };

  const handleBackToHome = () => {
    setGameStarted(false);
  };

  // Floating elements
  const FloatingElements = () => {
    return (
      <div className="floating-elements-container">
        {[...Array(10)].map((_, i) => (
          <div 
            key={`letter-${i}`} 
            className={`floating-element letter letter-${i+1}`}
            style={{ 
              transform: `translate(
                ${(mousePosition.x / window.innerWidth - 0.5) * (-15 - i * 7 - Math.random() * 10)}px, 
                ${(mousePosition.y / window.innerHeight - 0.5) * (-15 - i * 6 - Math.random() * 10)}px
              )` 
            }}
          />
        ))}
        {[...Array(5)].map((_, i) => (
          <div 
            key={`cloud-${i}`} 
            className={`floating-element cloud cloud-${i+1}`}
            style={{ 
              transform: `translate(
                ${(mousePosition.x / window.innerWidth - 0.5) * (-30 + i * 8 + Math.random() * 5)}px, 
                ${(mousePosition.y / window.innerHeight - 0.5) * (-30 + i * 7 + Math.random() * 5)}px
              )` 
            }}
          />
        ))}
        {[...Array(4)].map((_, i) => (
          <div 
            key={`dice-${i}`} 
            className={`floating-element dice dice-${i+1}`}
            style={{ 
              transform: `translate(
                ${(mousePosition.x / window.innerWidth - 0.5) * (-25 - i * 9 - Math.random() * 5)}px, 
                ${(mousePosition.y / window.innerHeight - 0.5) * (-25 + i * 8 + Math.random() * 5)}px
              )` 
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="app-container">
      {!gameStarted && <FloatingElements />}
      {!gameStarted ? (
        <HomePage 
          onStartSinglePlayer={handleStartSinglePlayer}
          onStartMultiPlayer={handleStartMultiPlayer}
        />
      ) : (
        <>
          <div className="game-header">
            <h1>Welcome, {userInfo.username}!</h1>
            <div className="header-buttons">
              <button className="nav-button">
                Mode: {gameMode === 'single' ? 'Single Player' : 'Multiplayer'}
              </button>
              <button 
                className="nav-button" 
                onClick={() => {
                  const trigger = document.querySelector('.scene-selector-trigger');
                  const button = document.querySelector('.nav-button:nth-child(2)');
                  if (trigger) {
                    // Create a custom event with button position info
                    const rect = button.getBoundingClientRect();
                    const event = new CustomEvent('sceneSelectorTrigger', { 
                      detail: { 
                        top: rect.bottom,
                        left: rect.left,
                        width: rect.width
                      } 
                    });
                    window.dispatchEvent(event);
                    trigger.click();
                  }
                }}
              >
                Learning Scene: {userInfo.currentScene || 'IELTS'}
              </button>
              <button 
                className="nav-button" 
                onClick={() => setShowRules(!showRules)}
              >
                Game Rules
              </button>
              <button className="nav-button" onClick={handleBackToHome}>
                Back to Home
              </button>
            </div>
          </div>
          <TalktasticVoiceGame 
            initialScene={userInfo.currentScene || 'IELTS'} 
            showRules={showRules}
            setShowRules={setShowRules}
          />
        </>
      )}
    </div>
  );
}

export default App;
